import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Truck, Leaf, Calendar } from "lucide-react";
import api from "../lib/api";
import { useFmt } from "../context/CurrencyContext";
import { resolveImageUrl } from "../components/ImageUpload";
import { sanitizeHTML } from "../components/RichTextEditor";
import StarRating from "../components/StarRating";
import SEO from "../components/SEO";

// Render landing CMS rich text safely
const RT = ({ html, as: Tag = "span", className = "", style = {} }) => (
  <Tag className={className} style={style} dangerouslySetInnerHTML={{ __html: sanitizeHTML(html || "") }} />
);

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [lp, setLp] = useState(null);
  const fmtJMD = useFmt();

  useEffect(() => {
    api.get("/products").then((r) => setFeatured(r.data.slice(0, 6))).catch(() => {});
    api.get("/preset-boxes").then((r) => setBoxes(r.data)).catch(() => {});
    api.get("/landing").then((r) => setLp(r.data)).catch(() => {});
  }, []);

  if (!lp) return <div className="min-h-[70vh]" />;

  return (
    <div data-testid="home-page">
      <SEO
        title={lp.seo_title || undefined}
        description={lp.seo_description || "Artisan Jamaican fruit popsicles delivered across Kingston and Saint Catherine."}
        image={resolveImageUrl(lp.seo_og_image || lp.hero_image_url)}
      />
      {/* Hero */}
      <section className="relative overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 py-12 sm:py-20 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <RT html={lp.hero_tagline} className="overline text-zinc-500 mb-4 sm:mb-6 block" data-testid="hero-tagline" />
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[1.05] break-words">
              <RT html={lp.hero_title_line1} /><br />
              <RT html={lp.hero_accent_word} style={{ color: "#F74B64" }} />{" "}
              <RT html={lp.hero_title_line2} />
            </h1>
            <RT html={lp.hero_subtitle} as="p" className="mt-6 sm:mt-8 text-base sm:text-lg text-zinc-600 max-w-xl leading-relaxed" />
            <div className="mt-8 sm:mt-10 flex flex-wrap gap-3 sm:gap-4">
              <Link to="/build" className="btn-primary inline-flex items-center gap-2" data-testid="hero-cta-build">
                <RT html={lp.hero_cta_primary} /> <ArrowRight size={16} />
              </Link>
              <Link to="/boxes" className="btn-ghost" data-testid="hero-cta-preset">
                <RT html={lp.hero_cta_secondary} />
              </Link>
            </div>
            <div className="mt-10 sm:mt-14 flex flex-wrap gap-x-6 gap-y-3 text-sm">
              <div className="flex items-center gap-2 text-zinc-700"><Truck size={16} style={{ color: "#F74B64" }} /> Kingston & St. Catherine</div>
              <div className="flex items-center gap-2 text-zinc-700"><Calendar size={16} style={{ color: "#FFA62B" }} /> Thu & Sat delivery</div>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="rounded-3xl overflow-hidden shadow-2xl"
              data-testid="hero-image">
              <img src={resolveImageUrl(lp.hero_image_url)}
                   alt="Artisan fruit popsicles"
                   className="w-full h-72 sm:h-96 lg:h-[520px] object-cover" />
            </motion.div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl px-5 py-4 border border-zinc-100 hidden md:flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "#FFF0E7" }}>
                <RT html={lp.hero_badge_number} className="font-display font-bold" style={{ color: "#FFA62B" }} />
              </div>
              <div>
                <RT html={lp.hero_badge_title} className="font-display font-bold text-sm block" />
                <RT html={lp.hero_badge_subtitle} className="text-xs text-zinc-500 block" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Preset boxes */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 py-14 sm:py-20 md:py-24">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 sm:gap-6 mb-10 sm:mb-12">
          <div>
            <RT html={lp.boxes_overline} className="overline text-zinc-500 mb-3 block" />
            <RT html={lp.boxes_heading} as="h2" className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" />
          </div>
          <Link to="/boxes" className="text-sm font-semibold flex items-center gap-1 hover:text-[#F74B64]" data-testid="home-see-all-boxes">
            See all boxes <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {boxes.map((b) => (
            <Link key={b.id} to="/boxes" className="group" data-testid={`home-box-${b.id}`}>
              <div className="rounded-2xl overflow-hidden bg-white border border-zinc-100 hover:shadow-xl transition-all">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={resolveImageUrl(b.image_url)} alt={b.name}
                       className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6">
                  <div className="font-display font-bold text-xl">{b.name}</div>
                  <div className="text-sm text-zinc-500 mt-1">{b.flavour_names.join(" · ")}</div>
                  <div className="mt-4 flex items-center justify-between">
                    <div className="font-display font-bold text-lg">{fmtJMD(b.price)}</div>
                    <span className="text-xs font-semibold text-[#F74B64]">Shop →</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured flavours */}
      <section className="bg-zinc-50 py-14 sm:py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 sm:gap-6 mb-10 sm:mb-12">
            <div>
              <RT html={lp.flavours_overline} className="overline text-zinc-500 mb-3 block" />
              <RT html={lp.flavours_heading} as="h2" className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" />
            </div>
            <Link to="/shop" className="text-sm font-semibold flex items-center gap-1 hover:text-[#F74B64]" data-testid="home-see-all-flavours">
              Shop all flavours <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {featured.map((p) => (
              <Link key={p.id} to={`/product/${p.id}`} className="group" data-testid={`home-flavour-${p.id}`}>
                <div className="aspect-square rounded-2xl overflow-hidden bg-white border border-zinc-100 group-hover:shadow-lg transition">
                  <img src={resolveImageUrl(p.image_url)} alt={p.name}
                       className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="mt-3">
                  <div className="font-semibold text-sm">{p.name}</div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs text-zinc-500">{fmtJMD(p.price)}</div>
                    {(p.rating_count || 0) > 0 && (
                      <StarRating rating={p.rating_avg || 0} size={11} showNumber count={p.rating_count} />
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Delivery info */}
      <section id="delivery" className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 py-14 sm:py-20 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 items-center">
          <div>
            <RT html={lp.delivery_overline} className="overline text-zinc-500 mb-3 block" />
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              <RT html={lp.delivery_heading_part1} /> <RT html={lp.delivery_heading_accent} style={{ color: "#FFA62B" }} />
            </h2>
            <RT html={lp.delivery_subtext} as="p" className="mt-5 sm:mt-6 text-zinc-600 text-base sm:text-lg leading-relaxed" />
            <p className="mt-4 text-sm text-zinc-500 leading-relaxed">
              Delivery fees are confirmed by our team once we review your order and your drop-off location.
            </p>
          </div>
          <div className="rounded-3xl overflow-hidden">
            <img src={resolveImageUrl(lp.delivery_image_url)}
                 alt="Jamaican tropical lifestyle"
                 className="w-full h-72 sm:h-96 lg:h-[460px] object-cover" />
          </div>
        </div>
      </section>
    </div>
  );
}
